import { Link } from 'react-router-dom'
import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Users, BarChart3, FileText, ShieldCheck } from 'lucide-react'

// §0.1 audit results — tiles shown only for features confirmed live
// LIVE: review_requests table, library_items table, hide_echosome_branding column, workspaces table, HoursSavedPage, methodology pages
// NOT LIVE: annual invoicing — removed from plan bullets below

const TWIN_PANEL_STEPS = [
  { step: '1', label: 'Upload survey data', desc: 'CSV from any panel provider. Echosome builds one twin per respondent.' },
  { step: '2', label: 'Backtest runs automatically', desc: 'Deterministic 80/20 holdout. Accuracy reported per twin with Wilson 95% CI.' },
  { step: '3', label: 'Run bulk queries', desc: 'Ask new questions across the panel. Distribution + per-segment cross-tab in ~40 seconds.' },
  { step: '4', label: 'Ship the deliverable', desc: 'White-labeled client report with twin-panel sections. Your branding, your methodology.' },
]

const AGENCY_TILES = [
  {
    title: 'Twin panel',
    desc: '1:1 modeling per respondent. Backtest-validated accuracy before any client deliverable.',
  },
  {
    title: 'Review queue',
    desc: 'Junior runs, senior reviews, ships to client. Every step recorded.',
  },
  {
    title: 'Methodology pages',
    desc: 'Customize the appendix on every client report with your firm\'s approach.',
  },
  {
    title: 'Reusable libraries',
    desc: 'Promote any segment, theme, or quote to a library. Next project pulls from it automatically.',
  },
  {
    title: 'White-label reports',
    desc: 'Hide Echosome branding. Use the client\'s logo. Twin-panel sections included. Agency Studio only.',
  },
  {
    title: 'Client portal',
    desc: 'Read-only workspace for your client. They see pre-reads, twin accuracy, validation outcomes, and progress — but can\'t edit or run new ones.',
  },
  {
    title: 'Per-project metering',
    desc: 'Track twins built and bulk queries run per engagement. Bill ad-hoc work outside the initial scope.',
  },
  {
    title: 'Hours-saved dashboard',
    desc: 'Show your principal how many fielding-hours your team saved this month, with the dollar value at your blended rate.',
  },
]

const BUYERS = [
  {
    role: 'Boutique research agency owner',
    desc: 'Your firm\'s IP gets stronger as projects accumulate. Switch-costs make clients stickier.',
  },
  {
    role: 'Research ops lead at a mid-market agency',
    desc: 'Onboarding new researchers drops from weeks to days. The library is the institutional memory.',
  },
  {
    role: 'Insights consultancy partner',
    desc: 'Pre-reads earn fees that used to require fielding. Margin improves on recurring engagements.',
  },
]

export function AgenciesPage() {
  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-16 space-y-16">

        {/* Hero */}
        <div className="space-y-5 max-w-3xl">
          <p className="text-sm font-medium text-echo-indigo uppercase tracking-wide">For agencies</p>
          <h1 className="text-4xl font-medium text-deep-ink leading-tight" style={{ letterSpacing: '-0.02em' }}>
            The research-modeling engine behind your client engagements.
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            Build 1:1 digital twins from your client's survey data. Backtest accuracy per respondent. Run bulk queries in seconds. Ship white-labeled deliverables that are defensible under scrutiny — without pretending AI replaces human validation.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/signup?plan=agency_studio" data-cta="primary">
              <Button variant="primary" size="lg">Start a 30-day Agency Studio trial</Button>
            </Link>
            <Link to="/demo" data-cta="secondary">
              <Button variant="outline" size="lg">See a live demo</Button>
            </Link>
          </div>
        </div>

        {/* Agency Economics */}
        <section className="space-y-6">
          <p className="text-sm font-medium text-echo-indigo uppercase tracking-wide">Agency economics</p>
          <h2 className="text-2xl font-medium text-deep-ink">
            Answer questions between waves — without fielding
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Once your client's data is archived, their twin panel answers new questions in ~40 seconds.
            No extra fieldwork. No wait. Every between-wave query is a billable deliverable.
          </p>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="mt-1 h-5 w-5 rounded-full bg-echo-indigo/10 flex items-center justify-center flex-shrink-0">
                <span className="h-2 w-2 rounded-full bg-echo-indigo" />
              </span>
              <div>
                <p className="text-sm font-medium text-deep-ink">Between-waves billable answers</p>
                <p className="text-sm text-slate-600">Run client questions any time without scheduling a new field wave. Each run is a deliverable your client pays for.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 h-5 w-5 rounded-full bg-echo-indigo/10 flex items-center justify-center flex-shrink-0">
                <span className="h-2 w-2 rounded-full bg-echo-indigo" />
              </span>
              <div>
                <p className="text-sm font-medium text-deep-ink">Archive value compounds over time</p>
                <p className="text-sm text-slate-600">Every project you archive earns indefinitely. A tracker study from 18 months ago can answer a new strategy question today.</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-1 h-5 w-5 rounded-full bg-echo-indigo/10 flex items-center justify-center flex-shrink-0">
                <span className="h-2 w-2 rounded-full bg-echo-indigo" />
              </span>
              <div>
                <p className="text-sm font-medium text-deep-ink">No fielding cost on re-runs</p>
                <p className="text-sm text-slate-600">Marginal cost per additional question after the first wave is near zero — your margin on follow-on queries is high.</p>
              </div>
            </li>
          </ul>
        </section>

        {/* Twin Panel — headline capability */}
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-echo-indigo" />
              <h2 className="text-2xl font-medium text-deep-ink">Twin Panel — per-respondent 1:1 modeling</h2>
            </div>
            <p className="text-slate-600 max-w-2xl">
              Upload a survey CSV. Echosome builds one twin per respondent, backtests accuracy against held-out human answers, and lets your team run bulk queries before the fieldwork budget is committed.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {TWIN_PANEL_STEPS.map(s => (
              <div key={s.step} className="bg-soft-panel border border-border-gray rounded-xl p-4 space-y-2">
                <div className="w-6 h-6 rounded-full bg-echo-indigo text-white text-xs font-medium flex items-center justify-center shrink-0">{s.step}</div>
                <p className="font-medium text-deep-ink text-sm">{s.label}</p>
                <p className="text-xs text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <BarChart3 size={16} className="text-echo-indigo mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-deep-ink">Backtest-validated accuracy</p>
                <p className="text-xs text-slate-600">Wilson 95% CI per twin. Mean accuracy 70–78% on typical 1,200-respondent studies.</p>
                <p className="text-xs text-slate-400 italic mt-1">
                  Accuracy is measured per-project from held-out respondent answers. Decision support only —
                  final review stays with your team.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <FileText size={16} className="text-echo-indigo mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-deep-ink">White-labeled deliverables</p>
                <p className="text-xs text-slate-600">Your branding. Your methodology prepends ours. Every prediction cites real prior responses.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <ShieldCheck size={16} className="text-echo-indigo mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-deep-ink">Audit trail</p>
                <p className="text-xs text-slate-600">Every twin's accuracy is inspectable. Defensible to your client's legal and compliance teams.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Why agencies pick Echosome */}
        <div className="space-y-4">
          <h2 className="text-2xl font-medium text-deep-ink">Why agencies pick Echosome</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Card>
              <h3 className="font-medium text-deep-ink mb-2">Margin</h3>
              <p className="text-sm text-slate-600">A senior researcher's preflight goes from days to under an hour. Your team ships more decisions per quarter without adding heads.</p>
            </Card>
            <Card>
              <h3 className="font-medium text-deep-ink mb-2">Differentiation</h3>
              <p className="text-sm text-slate-600">Your methodology appendix prepends Echosome's on every client report. Clients see your firm's IP, not just ours.</p>
            </Card>
            <Card>
              <h3 className="font-medium text-deep-ink mb-2">Team scaling</h3>
              <p className="text-sm text-slate-600">Junior researchers ramp on a year of accumulated evidence in an afternoon. The Library pulls in segments and themes from past projects automatically.</p>
            </Card>
          </div>
        </div>

        {/* Defensibility */}
        <section className="space-y-4">
          <p className="text-sm font-medium text-echo-indigo uppercase tracking-wide">Defensibility</p>
          <h2 className="text-2xl font-medium text-deep-ink">
            Every answer is auditable — by you, your client, or their legal team
          </h2>
          <p className="text-slate-600 leading-relaxed">
            Each twin is built from one respondent's actual answers. Accuracy is backtested against a
            held-out set of real responses before any deliverable is generated. The full miss list is
            always available — not hidden. Your clients can see exactly where the model was wrong.
          </p>
          <div className="border border-echo-indigo/20 rounded-xl p-5 bg-echo-indigo/5 space-y-2">
            <p className="text-sm font-medium text-deep-ink">Audit-ready backtest report</p>
            <p className="text-sm text-slate-600">
              Every project generates a per-twin accuracy report: scored vs. abstained counts, Wilson 95% CIs,
              and the full miss list. Send it to your client's legal and compliance teams as part of your deliverable.
            </p>
            <p className="text-xs text-slate-400 italic">
              Decision support only — final review stays with your team.
            </p>
          </div>
        </section>

        {/* Agency feature tiles */}
        <div className="space-y-4">
          <h2 className="text-2xl font-medium text-deep-ink">Agency-specific features</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {AGENCY_TILES.map(tile => (
              <Card key={tile.title}>
                <h3 className="font-medium text-deep-ink mb-2 text-sm">{tile.title}</h3>
                <p className="text-sm text-slate-600">{tile.desc}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Who buys this */}
        <div className="space-y-4">
          <h2 className="text-2xl font-medium text-deep-ink">Who buys this</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {BUYERS.map(b => (
              <Card key={b.role}>
                <h3 className="font-medium text-deep-ink mb-2 text-sm">{b.role}</h3>
                <p className="text-sm text-slate-600">{b.desc}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Plans */}
        <div className="space-y-4">
          <h2 className="text-2xl font-medium text-deep-ink">Plans for agencies</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Card className="space-y-2">
              <h3 className="font-medium text-deep-ink">Agency Pro <span className="text-slate-400 font-normal text-sm">$1,000–2,500 / month</span></h3>
              <p className="text-sm text-slate-600">Multiple client workspaces, evidence-backed pre-reads, validation planning, client-ready reports. Twin Panel up to 1,000 twins per project.</p>
              <Link to="/pricing">
                <Button variant="outline" size="sm" className="mt-2">See full pricing →</Button>
              </Link>
            </Card>
            <Card className="space-y-2">
              <h3 className="font-medium text-deep-ink">Agency Studio <span className="text-slate-400 font-normal text-sm">$3,000–7,500 / month</span></h3>
              <p className="text-sm text-slate-600">Everything in Agency Pro plus Twin Panel up to 10,000 twins per project, per-project metering, white-label reports with twin-panel sections, learning record, agreement scoring, priority support.</p>
              <Link to="/signup?plan=agency_studio">
                <Button variant="primary" size="sm" className="mt-2">Start 30-day trial</Button>
              </Link>
            </Card>
          </div>
        </div>

        {/* Pilot CTA */}
        <div className="border border-echo-indigo/20 rounded-xl p-6 bg-echo-indigo/5 space-y-3 mt-6">
          <h3 className="font-medium text-deep-ink">Run a pilot on one project</h3>
          <p className="text-sm text-slate-600">
            Upload one client CSV and run through the full twin-panel workflow. No contract. No credit card.
          </p>
          <Link to="/signup?intent=pilot">
            <Button variant="primary" size="sm">Start a pilot project</Button>
          </Link>
        </div>

        {/* Trust strip */}
        <div className="bg-soft-panel rounded-xl border border-border-gray p-6">
          <ul className="space-y-2">
            {[
              'Accuracy is measured, not assumed — every twin backtested before entering your deliverable.',
              'Evidence-backed, not synthetic — predictions cite real prior respondent answers.',
              'Risky segments routed to real humans via your panel provider.',
              'Decision support — final review stays with your legal and compliance teams.',
            ].map((bullet, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="text-echo-indigo mt-0.5 shrink-0">→</span>
                {bullet}
              </li>
            ))}
          </ul>
        </div>

      </main>
      <SiteFooter />
    </div>
  )
}
